"use client";

import React, { useState, useRef } from "react";
import dynamic from "next/dynamic";
import { m as motion, AnimatePresence } from "framer-motion";
import { Smartphone, Phone, MessageCircle, Github, Music } from "lucide-react";
import { getProjectsAsApps } from "@/lib/data";
import { StatusBar } from "./StatusBar";
import { WeatherWidget } from "./WeatherWidget";

export interface App {
  id: string;
  name: string;
  tagline?: string;
  logo?: string;
  screens?: string[];
}

const appsFromProjects: App[] = getProjectsAsApps().map(project => ({
  id: project.id || "",
  // Home-screen icons need just the app name (e.g. "Loopin"), not the full
  // marketing title ("Loopin - RPG Habit Tracker") - the full title still
  // shows in the detail panel via `tagline` once an app is selected.
  name: project.title.split(" - ")[0],
  tagline: project.tagline,
  logo: project.logo,
  screens: project.images,
}));

const AppStoreView = dynamic(() => import("./AppStoreView").then((m) => m.AppStoreView), { ssr: false });
const ContactApp = dynamic(() => import("./ContactApp").then((m) => m.ContactApp), { ssr: false });
const MessageApp = dynamic(() => import("./MessageApp").then((m) => m.MessageApp), { ssr: false });
const ControlCenter = dynamic(() => import("./ControlCenter").then((m) => m.ControlCenter), { ssr: false });
const CalendarWidget = dynamic(() => import("./CalendarWidget").then((m) => m.CalendarWidget), { ssr: false });
const SpotifyWidget = dynamic(() => import("./SpotifyWidget").then((m) => m.SpotifyWidget), { ssr: false });
const GitHubWidget = dynamic(() => import("./GitHubWidget").then((m) => m.GitHubWidget), { ssr: false });
const GitHubApp = dynamic(() => import("./GitHubApp").then((m) => m.GitHubApp), { ssr: false });
const SpotifyApp = dynamic(() => import("./SpotifyApp").then((m) => m.SpotifyApp), { ssr: false });
const TicTacToeApp = dynamic(() => import("./TicTacToeApp").then((m) => m.TicTacToeApp), { ssr: false });

export const MobileOSView = React.memo(({ onAppLaunch, currentApp, apps }: { onAppLaunch?: (app: App | null) => void; currentApp?: App | null; apps?: App[] }) => {
  const [internalActiveApp, setInternalActiveApp] = useState<App | null>(null);
  const [systemApp, setSystemApp] = useState<"contact" | "message" | "github" | "spotify" | "tictactoe" | null>(null);
  const [isControlCenterOpen, setIsControlCenterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  
  const displayApps = apps || appsFromProjects;
  const activeApp = currentApp !== undefined ? currentApp : internalActiveApp;
  
  const carouselRef = useRef<HTMLDivElement>(null);

  const handleAppLaunch = (app: App | null) => {
    if (onAppLaunch) onAppLaunch(app);
    else setInternalActiveApp(app);
  };

  const handleDragEnd = (event: any, info: any) => {
    // Safety delay for click prevention
    setTimeout(() => setIsDragging(false), 100);

    const swipeThreshold = 50;
    const velocityThreshold = 400;
    const { offset, velocity } = info;

    if (offset.x < -swipeThreshold || velocity.x < -velocityThreshold) {
      setCurrentPage(1);
    } else if (offset.x > swipeThreshold || velocity.x > velocityThreshold) {
      setCurrentPage(0);
    }
  };

  return (
    <div className="w-full h-full bg-black overflow-hidden font-sans text-white flex flex-col relative select-none">
      <div
        className="absolute inset-0 bg-cover bg-center transition-all duration-500"
        style={{
          backgroundImage: "url('https://i.postimg.cc/rpb6Lq81/4950c679bfcaba85756f80d2d3b57785.jpg')",
          filter: activeApp || systemApp ? "scale(1.1) blur(10px)" : "none",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/60" />

      <StatusBar onClick={() => setIsControlCenterOpen(true)} />

      {isControlCenterOpen && <ControlCenter isOpen={isControlCenterOpen} onClose={() => setIsControlCenterOpen(false)} />}

      <AnimatePresence mode="wait">
        {activeApp ? (
          <motion.div key="app-view" initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 25, stiffness: 200 }} className="absolute inset-0 z-40 bg-white">
            <AppStoreView app={activeApp} onClose={() => handleAppLaunch(null)} />
          </motion.div>
        ) : systemApp === "contact" ? (
          <motion.div key="contact" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="absolute inset-0 z-40 bg-white">
            <ContactApp onClose={() => setSystemApp(null)} />
          </motion.div>
        ) : systemApp === "message" ? (
          <motion.div key="message" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="absolute inset-0 z-40 bg-white">
            <MessageApp onClose={() => setSystemApp(null)} />
          </motion.div>
        ) : systemApp === "github" ? (
          <motion.div key="github" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="absolute inset-0 z-40">
            <GitHubApp onClose={() => setSystemApp(null)} />
          </motion.div>
        ) : systemApp === "spotify" ? (
          <motion.div key="spotify" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="absolute inset-0 z-40">
            <SpotifyApp onClose={() => setSystemApp(null)} />
          </motion.div>
        ) : systemApp === "tictactoe" ? (
          <motion.div key="tictactoe" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="absolute inset-0 z-40">
            <TicTacToeApp onClose={() => setSystemApp(null)} />
          </motion.div>
        ) : (
          <motion.div key="home" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="relative z-10 w-full h-full flex flex-col overflow-hidden" ref={carouselRef}>
            <motion.div 
              className="flex-1 flex w-[200%] touch-none cursor-grab active:cursor-grabbing"
              drag="x"
              dragConstraints={carouselRef}
              dragElastic={0.15}
              dragMomentum={false}
              onDragStart={() => setIsDragging(true)}
              onDragEnd={handleDragEnd}
              animate={{ x: currentPage === 0 ? "0%" : "-50%" }}
              transition={{ type: "spring", damping: 28, stiffness: 250 }}
            >
              {/* PAGE 1 */}
              <div className="w-1/2 h-full shrink-0 pt-16 px-6 flex flex-col">
                <div className="space-y-10">
                  <div className="grid grid-cols-2 gap-5 h-36">
                    <CalendarWidget />
                    <WeatherWidget />
                  </div>
                  <div className="grid grid-cols-4 gap-y-12 gap-x-4">
                    {displayApps.slice(0, 8).map((app) => (
                      <motion.button
                        key={app.id}
                        onClick={() => !isDragging && handleAppLaunch(app)}
                        whileTap={{ scale: 0.9 }}
                        className="flex flex-col items-center gap-2 group w-full"
                      >
                        <div className="w-full aspect-square max-w-[6.2rem] rounded-[22%] bg-zinc-800 shadow-xl shadow-black/40 flex items-center justify-center ring-1 ring-white/10 overflow-hidden transition-all group-hover:ring-white/30">
                          {app.logo || app.screens?.[0] ?
                            <img src={app.logo || app.screens?.[0]} alt={app.name} className="w-full h-full object-cover" /> :
                            <Smartphone className="w-6 h-6 text-white" />
                          }
                        </div>
                        <span className="text-[clamp(0.55rem,3cqw,0.8125rem)] font-bold text-white tracking-tight line-clamp-2 break-words w-full text-center px-1 drop-shadow-md leading-tight">
                          {app.name}
                        </span>
                      </motion.button>
                    ))}
                  </div>
                  {displayApps.length > 8 && (
                    <div className="grid grid-cols-4 gap-y-12 gap-x-4">
                       {displayApps.slice(8, 12).map((app) => (
                         <motion.button key={app.id} onClick={() => !isDragging && handleAppLaunch(app)} whileTap={{ scale: 0.9 }} className="flex flex-col items-center gap-2 group w-full">
                           <div className="w-full aspect-square max-w-[6.2rem] rounded-[22%] bg-zinc-800 shadow-xl shadow-black/40 flex items-center justify-center ring-1 ring-white/10 overflow-hidden transition-all group-hover:ring-white/30">
                             {app.logo || app.screens?.[0] ? <img src={app.logo || app.screens?.[0]} alt={app.name} className="w-full h-full object-cover" /> : <Smartphone className="w-6 h-6 text-white" />}
                           </div>
                           <span className="text-[clamp(0.55rem,3cqw,0.8125rem)] font-bold text-white tracking-tight line-clamp-2 break-words w-full text-center px-1 drop-shadow-md leading-tight">{app.name}</span>
                         </motion.button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* PAGE 2 */}
              <div className="w-1/2 h-full shrink-0 pt-16 px-6 flex flex-col">
                <div className="space-y-10">
                  <div className="grid grid-cols-2 gap-5 h-36">
                    <SpotifyWidget onClick={() => !isDragging && setSystemApp("spotify")} />
                    <GitHubWidget onClick={() => !isDragging && setSystemApp("github")} />
                  </div>
                  <div className="grid grid-cols-4 gap-y-12 gap-x-4">
                    <motion.button onClick={() => !isDragging && setSystemApp("github")} whileTap={{ scale: 0.9 }} className="flex flex-col items-center gap-2 group w-full">
                      <div className="w-full aspect-square max-w-[6.2rem] rounded-[22%] bg-zinc-900 shadow-xl shadow-black/40 flex items-center justify-center ring-1 ring-white/10 overflow-hidden transition-all group-hover:ring-white/30">
                        <Github className="w-6 h-6 text-white" />
                      </div>
                      <span className="text-[clamp(0.55rem,3cqw,0.8125rem)] font-bold text-white tracking-tight line-clamp-2 break-words w-full text-center px-1 drop-shadow-md leading-tight">GitHub</span>
                    </motion.button>
                    <motion.button onClick={() => !isDragging && setSystemApp("tictactoe")} whileTap={{ scale: 0.9 }} className="flex flex-col items-center gap-2 group w-full">
                      <div className="w-full aspect-square max-w-[6.2rem] rounded-[22%] bg-gradient-to-br from-blue-500 to-indigo-600 shadow-xl shadow-black/40 flex items-center justify-center ring-1 ring-white/10 overflow-hidden transition-all group-hover:ring-white/30">
                        <div className="grid grid-cols-2 gap-[3px] w-2/5 aspect-square">
                           <div className="border-r-[1.5px] border-b-[1.5px] border-white/40" />
                           <div className="border-b-[1.5px] border-white/40" />
                           <div className="border-r-[1.5px] border-white/40" />
                           <div />
                        </div>
                      </div>
                      <span className="text-[clamp(0.55rem,3cqw,0.8125rem)] font-bold text-white tracking-tight line-clamp-2 break-words w-full text-center px-1 drop-shadow-md leading-tight">Tic Tac Toe</span>
                    </motion.button>
                  </div>
                  <div className="bg-white/5 backdrop-blur-md rounded-[1.5rem] p-5 border border-white/5 flex justify-between items-center shadow-xl">
                    <div className="space-y-1">
                      <p className="text-[11px] text-zinc-400 uppercase tracking-widest font-bold">System Status</p>
                      <p className="text-white text-sm font-medium">All Systems Operational</p>
                    </div>
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]" />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Search Pill */}
            <div className="flex justify-center mb-6">
               <div className="px-5 py-2.5 bg-white/10 backdrop-blur-md rounded-full border border-white/10 flex items-center gap-2">
                 <div className="w-1 h-1 rounded-full bg-white/40" />
                 <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">Search</span>
                 <div className="w-1 h-1 rounded-full bg-white/40" />
               </div>
            </div>

            <div className="flex justify-center items-center gap-2 mb-2 shrink-0 z-20">
              {[0, 1].map((i) => (
                <button 
                  key={i} 
                  onClick={() => setCurrentPage(i)} 
                  className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${currentPage === i ? "bg-white w-3" : "bg-white/30"}`} 
                />
              ))}
            </div>

            {/* Premium Dock */}
            <div className="mt-auto mb-8 mx-5 p-4 bg-white/15 backdrop-blur-2xl rounded-[2.8rem] flex justify-between items-center px-6 border border-white/20 shadow-2xl z-20">
              <button
                onClick={() => setSystemApp("contact")}
                className="w-[19%] aspect-square max-w-16 rounded-[1.3rem] bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center shadow-lg active:scale-90 transition-all border border-white/10"
              >
                <Phone className="w-[45%] h-[45%] fill-white text-white" />
              </button>
              <button
                onClick={() => setSystemApp("message")}
                className="w-[19%] aspect-square max-w-16 rounded-[1.3rem] bg-gradient-to-br from-[#00E676] to-[#00C853] flex items-center justify-center shadow-lg active:scale-90 transition-all border border-white/10"
              >
                <svg viewBox="0 0 24 24" className="w-[50%] h-[50%] fill-white" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.414 0 .018 5.393 0 12.03c0 2.122.554 4.193 1.607 6.013L0 24l6.135-1.61a11.74 11.74 0 005.911 1.586h.005c6.634 0 12.032-5.396 12.033-12.032a11.76 11.76 0 00-3.483-8.512z"/>
                </svg>
              </button>
              <button
                onClick={() => setSystemApp("spotify")}
                className="w-[19%] aspect-square max-w-16 rounded-[1.3rem] bg-gradient-to-br from-[#1DB954] to-[#1aa34a] flex items-center justify-center shadow-lg active:scale-90 transition-all border border-white/10"
              >
                <Music className="w-[45%] h-[45%] fill-white text-white" />
              </button>
              <button
                onClick={() => setSystemApp("github")}
                className="w-[19%] aspect-square max-w-16 rounded-[1.3rem] bg-gradient-to-br from-zinc-800 to-black flex items-center justify-center shadow-lg active:scale-90 transition-all border border-white/10"
              >
                <Github className="w-[45%] h-[45%] text-white" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

MobileOSView.displayName = "MobileOSView";
